import { defineComponent, computed, ref } from 'vue';
import { usePrefixClass, useConfig } from '@tdesign/shared-hooks';

import props from '../paragraph-props';
import TTooltip from '../../tooltip/index';

import type { TypographyEllipsis } from '../type';

export default defineComponent({
  name: 'TEllipsis',
  components: { TTooltip },
  props: {
    ...props,
    renderCopy: Function,
  },
  setup(props, { slots }) {
    const COMPONENT_NAME = usePrefixClass('typography');
    const { globalConfig } = useConfig('typography');

    const content = computed(() => {
      return props.content || slots?.default();
    });

    const ellipsisState = computed((): TypographyEllipsis => {
      const ellipsis = typeof props.ellipsis === 'object' ? { ...props.ellipsis } : {};
      return {
        row: 1,
        expandable: false,
        ...{ ...ellipsis, placement: 'top-right' },
      };
    });

    const ellipsisStyles = computed((): any => {
      const ellipsis = ellipsisState.value;
      const def = {
        overflow: props.ellipsis ? 'hidden' : 'visible',
        textOverflow: props.ellipsis ? 'ellipsis' : 'initial',
        whiteSpace: props.ellipsis ? 'normal' : 'nowrap',
        display: '-webkit-box',
        WebkitLineClamp: ellipsis.row,
        WebkitBoxOrient: 'vertical',
      };

      if (isExpand.value) {
        def.overflow = 'visible';
        def.whiteSpace = 'normal';
        def.display = 'initial';
      }
      return def;
    });
    const isExpand = ref(false);

    const onExpand = () => {
      isExpand.value = true;
      if (typeof props.ellipsis === 'object') props.ellipsis.onExpand?.(true);
    };

    const onCollapse = () => {
      isExpand.value = false;
      if (typeof props.ellipsis === 'object') props.ellipsis.onExpand?.(false);
    };

    const renderEllipsisExpand = () => {
      const { suffix } = ellipsisState.value;
      const symbolStyle = {
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flex: 1,
        marginRight: props.renderCopy ? '8px' : 0,
      };
      const moreNode = (
        <span class={`${COMPONENT_NAME.value}-ellipsis-symbol`} onClick={onExpand} style={symbolStyle}>
          {suffix || globalConfig.value.expandText}
        </span>
      );

      const { tooltipProps, expandable, collapsible } = ellipsisState.value;
      if (!isExpand.value && expandable) {
        return tooltipProps && tooltipProps.content ? (
          <TTooltip {...tooltipProps} content={tooltipProps.content}>
            {moreNode}
          </TTooltip>
        ) : (
          moreNode
        );
      }
      if (expandable && isExpand.value && collapsible) {
        return (
          <span class={`${COMPONENT_NAME.value}-ellipsis-symbol`} onClick={onCollapse} style={symbolStyle}>
            {globalConfig.value.collapseText}
          </span>
        );
      }
    };

    return () => {
      const { tooltipProps } = ellipsisState.value;
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {tooltipProps && <TTooltip {...tooltipProps}></TTooltip>}
          <p style={props.ellipsis ? ellipsisStyles.value : {}}>{content.value}</p>
          {renderEllipsisExpand()}
          {props.renderCopy?.()}
        </div>
      );
    };
  },
});
